import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Loader from '../../components/Loader';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import NotFound from '../../components/UI/NotFound';
import { HiOutlinePlus, HiOutlineRefresh, HiOutlineSearch } from 'react-icons/hi';
import { FaEdit, FaTrashAlt, FaCopy, FaToggleOn, FaImages, FaTimesCircle, FaDownload, FaSortAmountDown } from 'react-icons/fa';

const PAGE_LIMIT = 10;
const IMG_ACCEPT = 'image/png,image/webp,image/jpeg,image/jpg';
// Checkerboard so transparent frames read clearly
const CHECKER = {
    backgroundImage: 'linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
};

const downloadFile = async (url, filename) => {
    if (!url) return;
    try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error('network');
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl; a.download = filename || 'frame.png';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objUrl);
    } catch (e) {
        const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.click();
    }
};

// ---- Image dropzone: click / drag-drop / paste, with preview ----
const FrameDropzone = ({ file, onPick, hint }) => {
    const [drag, setDrag] = useState(false);
    const [active, setActive] = useState(false);
    const inputRef = useRef(null);
    const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

    const accept_ = (f) => {
        if (!f) return;
        if (!IMG_ACCEPT.split(',').includes(f.type)) { toast.error('Only PNG/WebP/JPG images.'); return; }
        onPick(f);
    };
    const acceptRef = useRef(accept_); acceptRef.current = accept_;

    useEffect(() => {
        if (!active) return;
        const onPaste = (e) => {
            const items = (e.clipboardData && e.clipboardData.items) || [];
            for (const it of items) { if (it.kind === 'file') { const f = it.getAsFile(); if (f) { acceptRef.current(f); break; } } }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [active]);

    return (
        <div
            role="button" tabIndex={0}
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files && e.dataTransfer.files[0]; accept_(f); }}
            onMouseEnter={() => setActive(true)} onMouseLeave={() => setActive(false)}
            onFocus={() => setActive(true)} onBlur={() => setActive(false)}
            className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition ${drag ? 'border-main-blue bg-main-blue/5' : 'border-gray-300 hover:border-main-blue/60'}`}
        >
            <input ref={inputRef} type="file" accept={IMG_ACCEPT} className="hidden"
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; accept_(f); }} />
            {url ? (
                <img src={url} alt="preview" className="mx-auto max-h-44 rounded-lg border border-gray-200 object-contain" style={CHECKER} />
            ) : (
                <div className="py-5 text-gray-500">
                    <p className="text-sm font-medium">Drag &amp; drop, click, or paste</p>
                    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
                </div>
            )}
        </div>
    );
};

// ---- Edit Modal ----
const EditModal = ({ frame, onClose, onSaved }) => {
    const [name, setName] = useState(frame.f_name || '');
    const [orderVal, setOrderVal] = useState(frame.f_order ?? 0);
    const [status, setStatus] = useState(frame.f_status);
    const [imageFile, setImageFile] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!name?.trim()) { setError('Name is required.'); return; }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('f_name', name.trim());
            fd.append('f_order', Number(orderVal) || 0);
            fd.append('f_status', status);
            if (imageFile) fd.append('image', imageFile);
            const resp = await Calling.updateInpixFrame(frame._id, fd);
            if (resp?.status) { toast.success(resp?.message); onSaved(); onClose(); }
            else toast.error(resp?.message);
        } catch (err) { toast.error('Something went wrong.'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-main-blue/10 rounded-lg"><FaEdit className="text-main-blue" /></div>
                        <h2 className="text-xl font-semibold text-gray-800">Edit Frame</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FaTimesCircle size={22} /></button>
                </div>

                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Current</label>
                        <img src={frame.f_thumbnail || frame.f_image} alt={frame.f_name} className="max-h-40 rounded-lg border border-gray-200 object-contain" style={CHECKER} />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Replace image (optional)</label>
                        <FrameDropzone file={imageFile} onPick={setImageFile} hint="PNG keeps transparency. Leave empty to keep current." />
                    </div>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Frame name" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Order</label>
                            <input type="number" min="0" value={orderVal} onChange={(e) => setOrderVal(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                            <label className="relative inline-flex items-center cursor-pointer mt-2">
                                <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                            </label>
                        </div>
                    </div>
                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>

                <div className="p-5 border-t border-gray-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
                    <button onClick={handleSave} disabled={saving}
                        className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2 disabled:opacity-50">
                        <FaEdit className="text-sm" />{saving ? 'Updating...' : 'Update'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const InpixFrame = () => {
    const [loader, setLoader] = useState(false);
    const [frames, setFrames] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('f_order');
    const [order, setOrder] = useState('asc');

    const [name, setName] = useState('');
    const [orderVal, setOrderVal] = useState(0);
    const [status, setStatus] = useState(true);
    const [imageFile, setImageFile] = useState(null);
    const [uploadKey, setUploadKey] = useState(0);
    const [error, setError] = useState(null);

    const [selected, setSelected] = useState([]);
    const [editFrame, setEditFrame] = useState(null);

    const fetchData = useCallback(async () => {
        setLoader(true);
        try {
            const resp = await Calling.getInpixFrame({ page, limit: PAGE_LIMIT, search, status: statusFilter, sortBy, order });
            if (resp?.status) {
                setFrames(resp?.data || []);
                setTotalRecords(resp?.totalRecords || 0);
                setTotalPages(resp?.totalPages || 1);
            } else {
                setFrames([]); setTotalRecords(0); setTotalPages(1);
                if (resp?.message) toast.error(resp.message);
            }
        } catch (err) { toast.error('Failed to fetch frames'); }
        finally { setLoader(false); }
    }, [page, search, statusFilter, sortBy, order]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const resetForm = () => { setName(''); setOrderVal(0); setStatus(true); setImageFile(null); setUploadKey((k) => k + 1); setError(null); };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!imageFile) return setError('Frame image is required.');
        if (!name?.trim()) return setError('Name is required.');
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('image', imageFile);
            fd.append('f_name', name.trim());
            fd.append('f_order', Number(orderVal) || 0);
            fd.append('f_status', status);
            const resp = await Calling.addInpixFrame(fd);
            if (resp?.status) { toast.success(resp?.message); resetForm(); fetchData(); }
            else toast.error(resp?.message);
        } catch (err) { toast.error('Something went wrong.'); }
        finally { setLoader(false); }
    };

    const handleToggleStatus = async (f) => {
        setLoader(true);
        try {
            const fd = new FormData();
            fd.append('f_status', !f.f_status);
            const resp = await Calling.updateInpixFrame(f._id, fd);
            if (resp?.status) { toast.success(resp?.message); fetchData(); }
            else toast.error(resp?.message);
        } catch (err) { toast.error('Failed to update status.'); }
        finally { setLoader(false); }
    };

    const handleDelete = async () => {
        if (selected.length === 0) return toast.error('Please select at least one frame to delete.');
        const check = generateCaptcha('You are about to delete selected frames. Solve this to confirm.');
        if (!check) return toast.error('Incorrect answer.');
        setLoader(true);
        try {
            const resp = await Calling.deleteInpixFrame({ id: selected });
            if (resp?.status) { toast.success(resp?.message); setSelected([]); fetchData(); }
            else toast.error(resp?.message);
        } catch (err) { toast.error('Failed to delete frames.'); }
        finally { setLoader(false); }
    };

    const handleSearch = (e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); };
    const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    const selectAll = (e) => setSelected(e.target.checked ? frames.map((f) => f._id) : []);
    const copyId = (id) => { navigator.clipboard.writeText(id); toast.success('ID copied!'); };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className="w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
                    <div className="mx-auto">
                        <div className="flex justify-between items-center mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Inpix Frames</h1>
                                <p className="mt-1 text-sm text-gray-500">Overlay/border frames (transparent PNG) — connect them to templates</p>
                            </div>
                            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white shadow-sm">
                                <HiOutlineRefresh className="text-lg" /> <span>Refresh</span>
                            </button>
                        </div>

                        {/* Add Card */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100 mb-6">
                            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
                                <div className="p-2.5 bg-main-blue/10 rounded-lg"><HiOutlinePlus className="text-main-blue text-xl" /></div>
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800">Add New Frame</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">PNG keeps transparency · compressed on upload</p>
                                </div>
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                <form onSubmit={handleAdd} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="md:col-span-2" key={uploadKey}>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Frame Image *</label>
                                        <FrameDropzone file={imageFile} onPick={setImageFile} hint="PNG / WebP / JPG · transparent PNG recommended" />
                                    </div>
                                    <div>
                                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaImages className="text-main-blue text-sm" /> Name</label>
                                        <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="e.g., Gold Border" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaSortAmountDown className="text-main-blue text-sm" /> Order</label>
                                            <input type="number" min="0" value={orderVal} onChange={(e) => setOrderVal(e.target.value)}
                                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" />
                                        </div>
                                        <div>
                                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1"><FaToggleOn className="text-main-blue text-sm" /> Status</label>
                                            <label className="relative inline-flex items-center cursor-pointer mt-2">
                                                <input type="checkbox" checked={status} onChange={() => setStatus(!status)} className="sr-only peer" />
                                                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                <span className="ml-3 text-sm font-medium text-gray-700">{status ? 'Active' : 'Inactive'}</span>
                                            </label>
                                        </div>
                                    </div>

                                    {error && <p className="md:col-span-2 text-xs text-red-500">{error}</p>}

                                    <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                                        <button type="button" onClick={resetForm} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">Reset</button>
                                        <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80 flex items-center gap-2">
                                            <HiOutlinePlus className="text-sm" /> Save Frame
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Toolbar */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
                            <div className="flex flex-col md:flex-row md:items-center gap-3">
                                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                                    <div className="relative flex-1">
                                        <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue" placeholder="Search by name..." />
                                    </div>
                                    <button type="submit" className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-main-blue/80">Search</button>
                                </form>

                                <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="">All Status</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>

                                <select value={`${sortBy}:${order}`} onChange={(e) => { const [s, o] = e.target.value.split(':'); setSortBy(s); setOrder(o); setPage(1); }}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue">
                                    <option value="f_order:asc">Order ↑</option>
                                    <option value="f_order:desc">Order ↓</option>
                                    <option value="f_name:asc">Name A–Z</option>
                                    <option value="createdAt:desc">Newest</option>
                                </select>
                            </div>
                        </div>

                        {/* List */}
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 bg-main-blue/10 rounded-lg"><FaImages className="text-main-blue text-xl" /></div>
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-800">Frames</h2>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            <span className="px-2 py-0.5 bg-main-blue/10 text-main-blue rounded-full text-xs font-medium">Total: {totalRecords}</span>
                                        </p>
                                    </div>
                                </div>
                                {selected.length > 0 && (
                                    <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                                        <FaTrashAlt className="text-sm" /> Delete Selected ({selected.length})
                                    </button>
                                )}
                            </div>

                            <div className="p-5 bg-gray-50/50">
                                {frames.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="px-4 py-3 text-left">
                                                        <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                            onChange={selectAll} checked={selected.length === frames.length && frames.length > 0} />
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frame</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Views</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {frames.map((f) => (
                                                    <tr key={f._id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-4">
                                                            <input type="checkbox" className="h-4 w-4 text-main-blue border-gray-300 rounded"
                                                                onChange={() => toggleSelect(f._id)} checked={selected.includes(f._id)} />
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <img src={f.f_thumbnail || f.f_image} alt={f.f_name}
                                                                className="w-12 h-12 object-contain rounded-md border border-gray-200" style={CHECKER}
                                                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{f.f_name}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{f.f_order}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{f.f_view_count ?? 0}</td>
                                                        <td className="px-4 py-4 whitespace-nowrap">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input type="checkbox" checked={f.f_status} onChange={() => handleToggleStatus(f)} className="sr-only peer" />
                                                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-main-blue"></div>
                                                            </label>
                                                        </td>
                                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                                            <div className="flex items-center gap-2">
                                                                <button onClick={() => setEditFrame(f)} className="flex items-center px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100" title="Edit"><FaEdit /></button>
                                                                <button onClick={() => downloadFile(f.f_image, `${(f.f_name || 'frame').replace(/\s+/g, '-')}.png`)} className="flex items-center px-2 py-1 bg-amber-100 text-amber-600 rounded-md hover:bg-amber-200" title="Download"><FaDownload /></button>
                                                                <button onClick={() => copyId(f._id)} className="flex items-center px-2 py-1 bg-green-100 text-green-600 rounded-md hover:bg-green-200" title="Copy ID"><FaCopy /></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div className="flex items-center justify-between mt-4 px-1">
                                            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                                                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <NotFound />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {editFrame && (
                <EditModal frame={editFrame} onClose={() => setEditFrame(null)} onSaved={fetchData} />
            )}
        </>
    );
};

export default InpixFrame;
